import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, EmptyState } from "@/components/Shell";
import StatusBadge from "@/components/StatusBadge";
import { ChevronLeft, RotateCcw, Check } from "lucide-react";

export default function RecoveryDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [rec, setRec] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [err, setErr] = useState("");
  const [whId, setWhId] = useState("");

  const load = () => api.get(`/recoveries/${id}`).then(r => setRec(r.data)).catch(e=>setErr(formatApiError(e)));
  useEffect(() => { load(); api.get("/warehouses").then(r => setWarehouses(r.data)); }, [id]);

  const receive = async () => {
    if (!whId) { setErr("Pick a warehouse"); return; }
    try { await api.post(`/recoveries/${id}/receive`, { warehouse_id: whId, remark: "" }); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  if (err) return <div className="p-6 text-rose-600 text-sm">{err}</div>;
  if (!rec) return <div className="p-6 text-sm text-zinc-500">Loading…</div>;

  const canReceive = ["admin", "warehouse_manager"].includes(user.role) && rec.status === "In Transit Recovery";

  return (
    <div className="p-4 md:p-8" data-testid="recovery-detail-page">
      <button onClick={()=>nav(-1)} className="text-xs text-zinc-500 hover:text-[#F97316] mb-3 inline-flex items-center gap-1"><ChevronLeft size={14}/> Back</button>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-widest font-bold text-[#F97316]">Recovery</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1 num">{rec.project_id || "—"}</h1>
          <div className="text-sm text-zinc-500 mt-1">By {rec.engineer_name} · From {rec.recovered_from} · {(rec.recovery_date||"").slice(0,10)}</div>
        </div>
        <StatusBadge status={rec.status} className="text-sm px-3 py-1" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-5 lg:col-span-2">
          <div className="font-display font-semibold mb-3">Recovered Items</div>
          <table className="w-full text-sm data-table">
            <thead className="bg-zinc-50">
              <tr>
                <th className="px-3 py-2 text-left">SKU</th>
                <th className="px-3 py-2 text-left">Product</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2 text-left">Condition</th>
                <th className="px-3 py-2 text-left">Serial Numbers</th>
              </tr>
            </thead>
            <tbody>
              {rec.items.map((it, i) => (
                <tr key={i} className="border-t border-zinc-100">
                  <td className="px-3 py-2 num">{it.sku}</td>
                  <td className="px-3 py-2">{it.product_name}</td>
                  <td className="px-3 py-2 num text-right font-bold">{it.quantity}</td>
                  <td className="px-3 py-2"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-zinc-100 text-zinc-700">{it.condition}</span></td>
                  <td className="px-3 py-2 num text-xs text-zinc-500">{(it.serial_numbers || []).join(", ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {canReceive && (
            <div className="mt-5 pt-5 border-t border-zinc-100" data-testid="recovery-receive-card">
              <div className="font-display font-semibold mb-3">Receive at Warehouse</div>
              <div className="flex gap-2">
                <select value={whId} onChange={e=>setWhId(e.target.value)} className="inp flex-1" data-testid="recovery-receive-wh">
                  <option value="">Pick warehouse to receive into</option>
                  {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
                <button onClick={receive} disabled={!whId} data-testid="recovery-receive-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm py-2 px-4 rounded-md inline-flex items-center gap-2 disabled:opacity-50"><Check size={14}/> Confirm Receive</button>
              </div>
              <div className="text-[11px] text-zinc-500 mt-2">Items will be moved to Available / Faulty / Repair / Scrap based on each item&apos;s condition.</div>
            </div>
          )}
        </Card>

        <Card className="p-5">
          <div className="font-display font-semibold mb-3">Details</div>
          <Detail k="Project/Circuit" v={rec.project_id || "—"} />
          <Detail k="Customer" v={rec.customer_name || "—"} />
          <Detail k="Site Address" v={rec.site_address || "—"} />
          <Detail k="Recovered From" v={rec.recovered_from} />
          <Detail k="Engineer" v={rec.engineer_name} />
          <Detail k="Recovery Date" v={(rec.recovery_date||"").slice(0,10) || "—"} />
          <Detail k="Returned To" v={rec.returned_to_warehouse_name || "—"} />
          <Detail k="Courier" v={rec.courier_details || "—"} />
          <Detail k="Remark" v={rec.remark || "—"} />
          {rec.received_warehouse_name && <Detail k="Received At" v={rec.received_warehouse_name} />}
          {rec.received_at && <Detail k="Received On" v={(rec.received_at||"").slice(0,19).replace("T"," ")} />}
        </Card>
      </div>
      <style>{`.inp { width:100%; border:1px solid #E4E4E7; background:#fff; border-radius:.375rem; padding:.5rem .75rem; font-size:.85rem; outline:none } .inp:focus { box-shadow: 0 0 0 2px #F97316; border-color: transparent }`}</style>
    </div>
  );
}

function Detail({ k, v }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-zinc-100 last:border-0 text-sm">
      <div className="text-zinc-500 text-xs uppercase tracking-wider font-semibold">{k}</div>
      <div className="text-right font-medium">{v}</div>
    </div>
  );
}
